import { BellOutlined } from '@ant-design/icons';
import { css, useTheme } from '@emotion/react';
import type { Theme } from '@emotion/react';
import { Draft, Inline, Stack, Text, useBreakpoint } from '@resi-media/resi-ui';
import { generatePath, useNavigate, useParams } from 'react-router';
import { P, match } from 'ts-pattern';
import { GOOGLE_EVENTS } from '@sites/constants/google-events';
import { HTML_ENTITIES } from '@sites/constants/html-entities';
import { HALF_HOUR, ONE_HOUR, LIVE_VIDEO_STATUS } from '@sites/constants/live-player';
import { useLivePlayer } from '@sites/contexts/livePlayerContext';
import { useQueryParams } from '@sites/contexts/queryContext';
import { usePrefix } from '@studio/hooks/use-prefix';
import { LiveJoinNowButton } from '../LiveJoinNowButton';

const S = {
  bannerContainer: (theme: Theme) => css`
    background-color: ${theme.palette.surface.table};
    border: 1px solid ${theme.palette.divider};
    border-radius: ${theme.shape.borderRadius.m};
    padding: ${theme.spacing.s} ${theme.spacing.m};
    max-height: ${theme.typography.pxToRem(64)};
    width: 100%;
  `,
  bellIcon: (theme: Theme, isLive: boolean) => css`
    color: ${theme.palette.neutral.text};
    ${isLive &&
    `
    animation: wiggle 1.5s ease-in-out infinite;
    transform-origin: center;

    @keyframes wiggle {
      0%,
      100% {
        transform: rotate(0deg);
      }
      10%,
      30%,
      50%,
      70%,
      90% {
        transform: rotate(-8deg);
      }
      20%,
      40%,
      60%,
      80% {
        transform: rotate(8deg);
      }`}
  `,
  timeNumber: (theme: Theme) => css`
    color: ${theme.palette.text.body};
    font-weight: ${theme.typography.fontWeight.bold};
  `,
  timeText: (theme: Theme) => css`
    color: ${theme.palette.text.body};
    font-weight: ${theme.typography.fontWeight.semiBold};
  `,
  truncateText: (_theme: Theme) => css`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    word-break: break-word;
    flex: 1;
    min-width: 0;
  `,
};

const LivePlayerBanner = () => {
  const { commonT, prefixNS } = usePrefix('components:', 'livePlayerBanner');
  const { countdownSeconds, isLive, liveVideoStatus, nextLiveEvent, showJoinEarly, webChannelId } = useLivePlayer();
  const { customerSite } = useParams<'customerSite'>();
  const utils = Draft.useUtils<Date>();
  const mediaQuery = useBreakpoint();
  const theme = useTheme();
  const { isEmbed } = useQueryParams();
  const navigate = useNavigate();

  const hrs = String(Math.floor(countdownSeconds / 3600)).padStart(2, '0');
  const mins = String(Math.floor((countdownSeconds % 3600) / 60)).padStart(2, '0');
  const secs = String(countdownSeconds % 60).padStart(2, '0');

  return (
    <Inline
      alignItems="center"
      css={S.bannerContainer}
      dataTestId="live-player-banner-test-id"
      id="live-player-banner-id"
      justifyContent="space-between"
      {...(isEmbed && { mt: { xs: 'x0', lg: 'l', xxl: 'x0' } })}
    >
      <Inline alignItems="center" gap={{ xs: 'xs', lg: 'm' }}>
        <BellOutlined css={(theme) => S.bellIcon(theme, isLive)} data-testid="bell-icon" />
        {mediaQuery.lg ? (
          <Stack dataTestId="live-player-banner-event-details-container" gap="xs">
            <Inline>
              <Text variant="h5" whiteSpace="nowrap">{`${prefixNS('liveBroadcast')}:`}</Text>
              <Text css={S.truncateText} variant="h5">
                {nextLiveEvent?.name}
              </Text>
            </Inline>
            <Inline>
              {nextLiveEvent?.startTime && (
                <Text variant="body2">
                  {utils.format(utils.parseISO(nextLiveEvent.startTime), 'normalDateWithWeekday')}
                </Text>
              )}
              <Text variant="body2">{HTML_ENTITIES.BULLET}</Text>
              {nextLiveEvent?.startTime && (
                <Text variant="body2">{utils.format(utils.parseISO(nextLiveEvent.startTime), 'fullTime12h')}</Text>
              )}
            </Inline>
          </Stack>
        ) : (
          <Text variant="h5">
            {match({ countdownSeconds, isLive })
              .with({ isLive: true }, () => prefixNS('liveBroadcast'))
              .with(
                { countdownSeconds: P.when((c) => c <= HALF_HOUR) },
                () => `${prefixNS('liveBroadcastIn')} ${mins}m ${secs}s`
              )
              .otherwise(() => `${prefixNS('liveBroadcastIn')}`)}
          </Text>
        )}
      </Inline>
      <Inline alignItems="center" dataTestId="time-text-container" gap="x5">
        {(mediaQuery.lg || countdownSeconds > HALF_HOUR) && !isLive && (
          <Inline gap="s">
            {countdownSeconds >= ONE_HOUR && (
              <Stack
                alignItems="center"
                dataTestId="live-player-banner-hours-container"
                gap={theme.typography.pxToRem(-4)}
              >
                <Text css={S.timeNumber} variant={mediaQuery.lg ? 'h3' : 'h4'}>
                  {hrs}
                </Text>
                <Text css={S.timeText} textAlign="center" variant="body4">
                  {commonT('hour_other')}
                </Text>
              </Stack>
            )}
            <Stack
              alignItems="center"
              dataTestId="live-player-banner-minutes-container"
              gap={theme.typography.pxToRem(-4)}
            >
              <Text css={S.timeNumber} variant={mediaQuery.lg ? 'h3' : 'h4'}>
                {mins}
              </Text>
              <Text css={S.timeText} textAlign="center" variant="body4">
                {commonT('minute_other')}
              </Text>
            </Stack>
            <Stack
              alignItems="center"
              dataTestId="live-player-banner-seconds-container"
              gap={theme.typography.pxToRem(-4)}
            >
              <Text css={S.timeNumber} variant={mediaQuery.lg ? 'h3' : 'h4'}>
                {secs}
              </Text>
              <Text css={S.timeText} textAlign="center" variant="body4">
                {commonT('second_other')}
              </Text>
            </Stack>
          </Inline>
        )}
        {liveVideoStatus !== LIVE_VIDEO_STATUS.ENDED && countdownSeconds <= HALF_HOUR && (
          <LiveJoinNowButton
            onClick={() => {
              navigate(
                generatePath('/:customerSite/live/:webChannelId', {
                  customerSite: customerSite ?? '',
                  webChannelId,
                })
              );
              gtag('event', GOOGLE_EVENTS.LIVE_STREAM_CLICK, { live_cta: 'banner' });
            }}
            showEndNode={mediaQuery.lg}
            showJoinEarly={showJoinEarly}
            sizeVariant={mediaQuery.lg ? 'm' : 's'}
          />
        )}
      </Inline>
    </Inline>
  );
};

LivePlayerBanner.displayName = 'LivePlayerBanner';

export { LivePlayerBanner };
